import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Children, forwardRef, isValidElement, ReactElement } from 'react';
import styled, { css } from 'styled-components';

import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';

export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverPortal = PopoverPrimitive.Portal;

// TODO: Investigate how we can get date popovers to work properly with the new date  picker
export const PopoverContent = PopoverPrimitive.PopoverContent;

const PopoverCloseButton = styled(PopoverPrimitive.Close)<{ isHeader?: boolean }>`
  ${({ isHeader }) =>
    !isHeader &&
    css`
      position: absolute;
      top: 8px;
      right: 8px;
    `}
  ${({ isHeader }) =>
    isHeader &&
    css`
      height: 16px;
    `}
  background: transparent;
  border: 0;
  cursor: pointer;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const PopoverClose = (
  props: PopoverPrimitive.PopoverCloseProps & { isHeader?: boolean },
) => {
  return (
    <PopoverCloseButton {...props}>
      <XIcon />
    </PopoverCloseButton>
  );
};

export interface PopoverCardProps extends PopoverPrimitive.PopoverContentProps {
  /**
   * Display X button to close popover
   */
  showCloseButton?: boolean;

  /**
   * Remove padding from Card
   */
  noPadding?: boolean;

  padding?: Spacing;

  /**
   * Text to display in the header of the card
   */
  heading?: string;
}

/**
 * Card to be used in Popover contexts. Inherits props from Radix UI Popper.Content.
 */
export const PopoverCard = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverCardProps
>(function PopoverCard({ children, showCloseButton, heading, noPadding, padding, ...props }, ref) {
  const header = heading && (
    <HeadingContainer>
      <Paragraph size='md' weight='bold' color='primary'>
        {heading}
      </Paragraph>
      {showCloseButton && <PopoverClose aria-label='Close' isHeader />}
    </HeadingContainer>
  );
  return (
    <PopoverContent asChild sideOffset={8} collisionPadding={8} {...props} ref={ref}>
      <Card elevated>
        {!heading && showCloseButton && <PopoverClose aria-label='Close' />}
        {noPadding ? (
          <>
            {heading && header}
            {children}
          </>
        ) : (
          <Card.Body padding={padding}>
            {heading && header}
            {children}
          </Card.Body>
        )}
      </Card>
    </PopoverContent>
  );
});

interface PopoverProps extends PopoverPrimitive.PopoverProps {
  /**
   * Component to use as trigger. Radix UI trigger component props will be merged with this element.
   */
  trigger?: ReactElement;

  /**
   * Props to apply to Popover.Trigger within
   */
  triggerProps?: PopoverPrimitive.PopoverTriggerProps;

  /**
   * An optional element to position the Popover.Content against.
   * If this part is not used, the content will position alongside the trigger.
   */
  anchor?: ReactElement<PopoverPrimitive.PopoverAnchorProps>;

  /**
   * Props to apply to Popover.Card within
   */
  contentProps?: PopoverCardProps;

  /**
   * Render content within a portal
   */
  portal?: boolean;

  /**
   * Props applied to Radix UI Portal component
   */
  portalProps?: PopoverPrimitive.PortalProps;
}

const Popover = ({
  children,
  trigger,
  triggerProps,
  contentProps,
  anchor,
  portal = true,
  portalProps,
  ...props
}: PopoverProps) => {
  let triggerElem;
  let contentElem;
  let anchorElem = anchor;

  const renderChildren = Children.toArray(children).filter((child) => {
    if (isValidElement(child)) {
      switch (child.type) {
        case PopoverPrimitive.Trigger:
          triggerElem = child;
          return false;
        case PopoverCard:
        case PopoverPrimitive.Content:
          contentElem = child;
          break;
        case PopoverPrimitive.Anchor:
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          anchorElem = child;
          return false;
      }
    }

    return true;
  });

  const content = contentElem || <PopoverCard {...contentProps}>{renderChildren}</PopoverCard>;

  return (
    <PopoverPrimitive.Root {...props}>
      {triggerElem ||
        (trigger && (
          <PopoverTrigger asChild {...triggerProps}>
            {trigger}
          </PopoverTrigger>
        ))}
      {anchorElem}
      {portal ? <PopoverPortal {...portalProps}>{content}</PopoverPortal> : content}
    </PopoverPrimitive.Root>
  );
};

export default Object.assign(Popover, {
  Trigger: PopoverTrigger,
  Anchor: PopoverAnchor,
  Portal: PopoverPortal,
  Content: PopoverContent,
  Card: PopoverCard,
  Close: PopoverClose,
});
